// `uib-datepicker-popup="dd/MM/yyyy"` from the usage-report form, ported.
//
// ⚠️ IT IS A TEXT INPUT, NOT `<input type="date">`, and the substitution is not cosmetic. The
// reference photographs this CLOSED, so the closed state is the entire pixel surface: a
// 48px-tall `.form-control` showing `dd/MM/yyyy`, the placeholder "Select date" when empty,
// and a `.date-icon` absolutely positioned 12px from the right. A native date input brings
// its own calendar glyph, its own locale-dependent format and its own intrinsic width, which
// moves every one of those.
//
// The popup is written against upstream's class names (`uib-datepicker-popup`,
// `uib-button-bar`, `uib-datepicker-current`, `uib-clear`) because the ported stylesheet
// styles those and nothing else.
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'

export interface DatePickerInputProps {
  id: string
  name: string
  value?: Date
  onChange: (value?: Date) => void
  /** Inclusive. A day before this cannot be chosen. */
  minDate?: Date
  /** Inclusive. A day after this cannot be chosen. */
  maxDate?: Date
  required?: boolean
}

/**
 * `dd/MM/yyyy`, zero-padded, in LOCAL time.
 *
 * ⚠️ NOT `toISOString().slice(0, 10)` reordered: that is UTC, and for most of the world it
 * names the previous day for anything in the evening. The dates here come from a calendar
 * the user clicked, so local is the only reading that round-trips.
 */
export function formatDdMmYyyy(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${String(date.getFullYear())}`
}

/** The inverse. Returns undefined for anything that is not a real `dd/MM/yyyy` day. */
export function parseDdMmYyyy(text: string): Date | undefined {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim())
  if (match === null) return undefined
  const [, dd, mm, yyyy] = match
  const day = Number(dd)
  const month = Number(mm)
  const year = Number(yyyy)
  const date = new Date(year, month - 1, day)
  // Rejects 31/02/2026, which `new Date` would roll forward into March rather than refuse.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined
  }
  return date
}

function atMidnight(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

function sameDay(a: Date, b: Date): boolean {
  return atMidnight(a) === atMidnight(b)
}

export function DatePickerInput({
  id,
  name,
  value,
  onChange,
  minDate,
  maxDate,
  required = false,
}: DatePickerInputProps): ReactElement {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState(value === undefined ? '' : formatDdMmYyyy(value))
  const [month, setMonth] = useState(() => value ?? new Date())
  const wrapper = useRef<HTMLDivElement>(null)

  // The input is controlled by the model while closed, but must not be rewritten under a
  // user who is mid-type. Syncing only when the model's formatted value differs from what is
  // shown keeps both true.
  useEffect(() => {
    const formatted = value === undefined ? '' : formatDdMmYyyy(value)
    setText((current) => (parseDdMmYyyy(current)?.getTime() === value?.getTime() ? current : formatted))
  }, [value])

  // ⚠️ Upstream stops propagation on the wrapper instead — `ng-click="$event.stopPropagation()"`,
  // commented HACK, "to stop datepicker from auto-closing unexpectedly". Same outcome, from
  // the other side: close on a click that was genuinely outside.
  useEffect(() => {
    if (!open) return undefined
    const onDocumentDown = (event: MouseEvent): void => {
      if (wrapper.current !== null && !wrapper.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocumentDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentDown)
    }
  }, [open])

  const disabled = (day: Date): boolean =>
    (minDate !== undefined && atMidnight(day) < atMidnight(minDate)) ||
    (maxDate !== undefined && atMidnight(day) > atMidnight(maxDate))

  const grid = useMemo(() => monthGrid(month), [month])

  const commit = (next?: Date): void => {
    onChange(next)
    setText(next === undefined ? '' : formatDdMmYyyy(next))
  }

  return (
    <div
      className="date-wrapper"
      ref={wrapper}
      // ⚠️ UPSTREAM'S OWN HACK, PORTED WITH ITS COMMENT. `generate-user-usage-report-form.tpl.html:6`
      // reads "HACK: ng-click=\"$event.stopPropagation()\" is used to stop datepicker from
      // auto-closing unexpectedly" — a click inside the wrapper must not reach whatever
      // dismisses the popup, or picking a date closes the thing you are picking it in.
      onClick={(event) => {
        event.stopPropagation()
      }}
    >
      <i className="date-icon fas fa-calendar-alt" />
      <input
        name={name}
        id={id}
        className={`form-control input date-input ${name === 'fromDate' ? 'from-date' : 'to-date'}${open ? ' focus' : ''}`}
        placeholder="Select date"
        value={text}
        required={required}
        onClick={() => {
          setOpen(true)
        }}
        onFocus={() => {
          setOpen(true)
        }}
        onChange={(event) => {
          setText(event.target.value)
          const parsed = parseDdMmYyyy(event.target.value)
          // An empty field clears the model — upstream deletes the timestamp from the
          // request rather than sending a null, and "no from date" is a real state.
          if (event.target.value.trim() === '') onChange(undefined)
          else if (parsed !== undefined && !disabled(parsed)) onChange(parsed)
        }}
      />
      {/* ⚠️ `display: block` INLINE, because this one is not a `uib-dropdown`. The datepicker
          popup is its own directive, and ui-bootstrap ships
          `.uib-datepicker-popup.dropdown-menu { display: block }` in its own stylesheet, which
          this port does not vendor — so the `.dropdown-menu` class alone hides the calendar
          exactly the way it hid the sort menu (see `uibDropdown`). `AddToJobPanel`'s typeahead
          list already uses the same escape, for the same reason. */}
      {open ? (
        <ul className="uib-datepicker-popup dropdown-menu" style={{ display: 'block' }} role="listbox">
          <li>
            <div className="uib-daypicker">
              <div className="uib-datepicker-nav">
                <button
                  type="button"
                  className="btn btn-default btn-sm pull-left uib-left"
                  onClick={() => {
                    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
                  }}
                >
                  <i className="fa fa-chevron-left" />
                </button>
                <strong>
                  {month.toLocaleDateString('en-US', { month: 'long' })} {month.getFullYear()}
                </strong>
                <button
                  type="button"
                  className="btn btn-default btn-sm pull-right uib-right"
                  onClick={() => {
                    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
                  }}
                >
                  <i className="fa fa-chevron-right" />
                </button>
              </div>
              <table className="uib-daypicker-table" role="grid">
                <thead>
                  <tr>
                    {/* `show-weeks="false"`, so no week-number column. */}
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                      <th key={d} scope="col">
                        <small aria-label={d}>{d}</small>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.map((week) => (
                    <tr key={week[0]?.toISOString()}>
                      {week.map((day) => {
                        const off = day.getMonth() !== month.getMonth()
                        const isDisabled = disabled(day)
                        return (
                          <td key={day.toISOString()} role="gridcell">
                            <button
                              type="button"
                              disabled={isDisabled}
                              className={`btn btn-default btn-sm${value !== undefined && sameDay(day, value) ? ' active' : ''}`}
                              onClick={() => {
                                commit(day)
                                setOpen(false)
                              }}
                            >
                              <span className={off || isDisabled ? 'text-muted' : ''}>{day.getDate()}</span>
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </li>
          <li className="uib-button-bar">
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-info uib-datepicker-current"
                onClick={() => {
                  const today = new Date()
                  if (!disabled(today)) {
                    commit(today)
                    setOpen(false)
                  }
                }}
              >
                Today
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger uib-clear"
                onClick={() => {
                  commit(undefined)
                  setOpen(false)
                }}
              >
                Clear
              </button>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-success uib-close"
              onClick={() => {
                setOpen(false)
              }}
            >
              Done
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  )
}

/** Six weeks starting on the Sunday on or before the first of the month, as uib does. */
function monthGrid(month: Date): Date[][] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const start = new Date(first)
  start.setDate(first.getDate() - first.getDay())
  const weeks: Date[][] = []
  for (let w = 0; w < 6; w += 1) {
    const week: Date[] = []
    for (let d = 0; d < 7; d += 1) {
      week.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7 + d))
    }
    weeks.push(week)
  }
  return weeks
}
